import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { TabGroup } from '@bi-studio/shared/lib';
import { getTabGroup, getReports, getCharts, getDashboards, getForms } from '@bi-studio/shared/apis';
import { RootState } from '../index';

interface TabGroupState {
    currentTabGroup: TabGroup | null;
    reports: { value: string; label: string }[];
    charts: { value: string; label: string }[];
    dashboards: { value: string; label: string }[];
    forms: { value: string; label: string }[];
    isLoading: boolean;
    isLoadingLists: boolean;
    isSaving: boolean;
    error: string | null;
}

const initialState: TabGroupState = {
    currentTabGroup: null,
    reports: [],
    charts: [],
    dashboards: [],
    forms: [],
    isLoading: false,
    isLoadingLists: false,
    isSaving: false,
    error: null,
};

export const fetchTabGroupDetails = createAsyncThunk(
    'tabGroup/fetchDetails',
    async ({ token, id }: { token: string; id: string }, { rejectWithValue }) => {
        try {
            const tabGroup = await getTabGroup(token, id);
            return tabGroup;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch tab group details');
        }
    }
);

export const fetchTabGroupSelectionLists = createAsyncThunk(
    'tabGroup/fetchSelectionLists',
    async (token: string, { rejectWithValue }) => {
        try {
            const [reportsData, chartsData, dashboardsData, formsData] = await Promise.all([
                getReports(token),
                getCharts(token),
                getDashboards(token),
                getForms(token),
            ]);

            // Transform data for selects
            const reportsList = Array.isArray(reportsData) ? reportsData : (reportsData as any).data || [];
            const chartsList = Array.isArray(chartsData) ? chartsData : (chartsData as any).data || [];
            const dashboardsList = Array.isArray(dashboardsData) ? dashboardsData : (dashboardsData as any).data || [];
            const formsList = Array.isArray(formsData) ? formsData : (formsData as any).data || [];

            return {
                reports: reportsList.map((r: any) => ({ value: r.id, label: r.name })),
                charts: chartsList.map((c: any) => ({ value: c.id, label: c.config?.title || `Chart ${c.id}` })),
                dashboards: dashboardsList.map((d: any) => ({ value: d.id, label: d.name })),
                forms: formsList.map((f: any) => ({ value: f.id, label: f.name })),
            };
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch selection lists');
        }
    }
);

export const tabGroupSlice = createSlice({
    name: 'tabGroup',
    initialState,
    reducers: {
        resetTabGroupState: (_state) => {
            return initialState;
        },
        setTabGroupError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setTabGroupSaving: (state, action: PayloadAction<boolean>) => {
            state.isSaving = action.payload;
        },
        clearCurrentTabGroup: (state) => {
            state.currentTabGroup = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Tab Group Details
            .addCase(fetchTabGroupDetails.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchTabGroupDetails.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentTabGroup = action.payload;
            })
            .addCase(fetchTabGroupDetails.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Fetch Selection Lists
            .addCase(fetchTabGroupSelectionLists.pending, (state) => {
                state.isLoadingLists = true;
            })
            .addCase(fetchTabGroupSelectionLists.fulfilled, (state, action) => {
                state.isLoadingLists = false;
                state.reports = action.payload.reports;
                state.charts = action.payload.charts;
                state.dashboards = action.payload.dashboards;
                state.forms = action.payload.forms;
            })
            .addCase(fetchTabGroupSelectionLists.rejected, (state, action) => {
                state.isLoadingLists = false;
                state.error = action.payload as string;
            });
    },
});

export const { resetTabGroupState, setTabGroupError, setTabGroupSaving, clearCurrentTabGroup } = tabGroupSlice.actions;

// Selectors
export const selectCurrentTabGroup = (state: RootState) => state.tabGroup.currentTabGroup;
export const selectTabGroupReports = (state: RootState) => state.tabGroup.reports;
export const selectTabGroupCharts = (state: RootState) => state.tabGroup.charts;
export const selectTabGroupDashboards = (state: RootState) => state.tabGroup.dashboards;
export const selectTabGroupForms = (state: RootState) => state.tabGroup.forms;
export const selectTabGroupLoading = (state: RootState) => state.tabGroup.isLoading;
export const selectTabGroupSaving = (state: RootState) => state.tabGroup.isSaving;
export const selectTabGroupListsLoading = (state: RootState) => state.tabGroup.isLoadingLists;
export const selectTabGroupError = (state: RootState) => state.tabGroup.error;

export default tabGroupSlice.reducer;
